import { useEffect, useMemo, useRef, useState } from "react";
import Layout from "../components/Layout";
import SectionBox from "../components/SectionBox";
import PageTitle from "../components/PageTitle";
import PageText from "../components/PageText";
import { fetchFleetStatsCached } from "../lib/fleetStatsCache";

const FLEETYARDS_EMBED_SCRIPT_ID = "fleetyards-embed-v2-script";
let fleetyardsScriptPromise = null;

function formatModelSlugFallback(slug) {
  if (typeof slug !== "string") return "";
  const trimmed = slug.trim();
  if (!trimmed) return "";

  return trimmed
    .split("-")
    .filter(Boolean)
    .map((part) => {
      const lower = part.toLowerCase();
      if (/^(i|ii|iii|iv|v|vi|vii|viii|ix|x)$/i.test(part)) return part.toUpperCase();
      if (lower === "mk") return "Mk";
      if (/^[a-z]+\d+[a-z0-9]*$/i.test(part)) return part.toUpperCase();
      if (/^\d+[a-z]+$/i.test(part)) return part.toUpperCase();
      if (/^[a-z]{1,2}$/i.test(part)) return part.toUpperCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
}

function sanitizeShipSlug(value) {
  if (typeof value !== "string") return "";
  return value
    .normalize("NFKD")
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
}

function buildFleetyardsShipSlug(ship) {
  const directSlug = sanitizeShipSlug(ship?.slug || "");
  if (directSlug) return directSlug;

  const name = String(ship?.name || "").trim();
  if (!name) return "";

  const manufacturer = String(ship?.manufacturer || "").trim();
  if (manufacturer && name.toLowerCase().startsWith(`${manufacturer.toLowerCase()} `)) {
    return sanitizeShipSlug(name.slice(manufacturer.length + 1));
  }

  return sanitizeShipSlug(name);
}

function FleetyardsAttribution({ className = "" }) {
  return (
    <p className={`text-xs text-white/70 ${className}`.trim()}>
      Fleet data and visualization provided by{" "}
      <a
        href="https://fleetyards.net"
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-2 decoration-white/70 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300/70 rounded-sm"
      >
        Fleetyards.net
      </a>
      .
    </p>
  );
}

function loadFleetyardsScriptOnce() {
  if (typeof window === "undefined") return Promise.resolve();
  if (fleetyardsScriptPromise) return fleetyardsScriptPromise;

  const existing = document.getElementById(FLEETYARDS_EMBED_SCRIPT_ID);
  if (existing) {
    fleetyardsScriptPromise = Promise.resolve();
    return fleetyardsScriptPromise;
  }

  fleetyardsScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = FLEETYARDS_EMBED_SCRIPT_ID;
    script.src = "https://fleetyards.net/embed-v2.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Fleetyards embed script"));
    (document.head || document.body).appendChild(script);
  });

  return fleetyardsScriptPromise;
}

function FleetCard({ ship }) {
  const [imageFailed, setImageFailed] = useState(false);
  const shipSlug = buildFleetyardsShipSlug(ship);
  const shipUrl = shipSlug ? `https://fleetyards.net/ships/${shipSlug}` : "";
  const imageUrl = !imageFailed && ship?.image_url ? ship.image_url : "";

  useEffect(() => {
    setImageFailed(false);
  }, [ship?.image_url]);

  return (
    <article className="bg-white/5 border border-white/10 rounded-lg p-4 transition duration-300 ease-out motion-reduce:transition-none transform-gpu hover:scale-[1.015] focus-within:scale-[1.015] hover:shadow-[0_0_0_1px_rgba(248,113,113,0.35),0_10px_30px_rgba(248,113,113,0.12)] focus-within:shadow-[0_0_0_1px_rgba(248,113,113,0.35),0_10px_30px_rgba(248,113,113,0.12)]">
      <div className="relative">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={ship.name}
            className="h-40 w-full object-cover rounded"
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <div className="h-40 w-full rounded bg-white/5 border border-white/10 flex items-center justify-center text-white/40 text-sm">
            No image available
          </div>
        )}
        <span className="absolute left-2 top-2 inline-flex items-center rounded-md border border-red-300/70 bg-black/80 px-2 py-1 text-xs font-orbitron font-semibold text-red-100 shadow-[0_0_14px_rgba(248,113,113,0.35)]">
          {Number(ship.total_count || 0).toLocaleString()}x
        </span>
      </div>
      <h3 className="mt-3 text-lg font-semibold">
        {shipUrl ? (
          <a
            href={shipUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 underline-offset-4 hover:underline focus-visible:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300/70 rounded-sm"
          >
            <span>{ship.name}</span>
          </a>
        ) : (
          ship.name
        )}
      </h3>
      <p className="text-white/70 text-sm">{ship.manufacturer || "Unknown"}</p>
      <p className="text-white/70 text-sm">{ship.classification || "—"}</p>
    </article>
  );
}

function FleetchartModal({ open, onClose, triggerRef, fleetId, ships }) {
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);
  const hasInitializedRef = useRef(false);
  const hasOpenedRef = useRef(false);
  const buttonObserverRef = useRef(null);
  const [scriptStatus, setScriptStatus] = useState("idle");

  const removeDefaultViewButton = () => {
    const root = document.getElementById("fleetyards-view");
    if (!root) return;

    const buttons = root.querySelectorAll("button");
    buttons.forEach((button) => {
      if (button.textContent?.trim().toLowerCase() === "default view") {
        button.remove();
      }
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    hasOpenedRef.current = true;

    const modalNode = modalRef.current;
    const focusable = () =>
      Array.from(
        modalNode?.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) || []
      );

    const triggerEl = triggerRef?.current || null;
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab") return;
      const nodes = focusable();
      if (nodes.length === 0) {
        event.preventDefault();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      triggerEl?.focus();
    };
  }, [open, onClose, triggerRef]);

  useEffect(() => {
    if (hasInitializedRef.current) return undefined;

    window.FleetYardsFleetchartConfig = {
      details: false,
      grouped: true,
      fleetchart: true,
      fleetchartGrouped: true,
      fleetchartScale: 25,
      groupedButton: true,
      fleetchartSlider: true,
      ships,
      users: [],
      fleetId,
    };

    setScriptStatus("loading");
    loadFleetyardsScriptOnce()
      .then(() => {
        hasInitializedRef.current = true;
        setScriptStatus("ready");
        removeDefaultViewButton();
        buttonObserverRef.current = new MutationObserver(() => {
          removeDefaultViewButton();
        });
        const root = document.getElementById("fleetyards-view");
        if (root) {
          buttonObserverRef.current.observe(root, { childList: true, subtree: true });
        }
      })
      .catch((error) => {
        console.error(error);
        setScriptStatus("error");
      });
    return () => {
      buttonObserverRef.current?.disconnect();
      buttonObserverRef.current = null;
    };
  }, [open, fleetId, ships]);

  useEffect(() => {
    if (!open) return;
    if (!hasInitializedRef.current) return;
    removeDefaultViewButton();
  }, [open]);

  return (
    <div
      className={`fixed inset-0 z-50 flex items-start justify-center p-4 pt-20 sm:pt-24 ${open ? "" : "pointer-events-none opacity-0"}`}
      role="presentation"
      aria-hidden={!open}
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        aria-label="Close Fleetchart dialog"
        onClick={onClose}
      />

      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="fleetchart-dialog-title"
        className="relative w-full max-w-6xl max-h-[calc(100vh-6rem)] overflow-hidden rounded-2xl border border-red-300/30 bg-slate-950/95 shadow-[0_30px_80px_rgba(0,0,0,0.65)]"
      >
        <div className="flex items-start justify-between gap-3 border-b border-white/10 px-5 py-4 sm:px-6">
          <div>
            <h2 id="fleetchart-dialog-title" className="font-orbitron text-xl text-white">Fleetchart</h2>
            <p className="text-sm text-white/70">Interactive fleet overview powered by Fleetyards.</p>
            <p className="mt-1 text-xs text-white/60">
              Note: this chart is large and may take a few moments to fully load.
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-sm text-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300/70"
          >
            Close
          </button>
        </div>

        <div className="overflow-auto p-4 sm:p-6 max-h-[calc(100vh-11rem)]">
          <div className="rounded-xl border border-white/10 bg-black/40 p-3 sm:p-4 min-h-[420px]">
            <div id="fleetyards-view" className="min-h-[390px]" />
            {scriptStatus === "loading" && hasOpenedRef.current ? <p className="mt-3 text-sm text-white/60">Loading Fleetchart…</p> : null}
            {scriptStatus === "error" ? (
              <p className="mt-3 text-sm text-rose-300">Failed to load Fleetchart. Please try again in a moment.</p>
            ) : null}
            <FleetyardsAttribution className="mt-3" />
            <noscript>
              Please enable JavaScript to view your custom Fleetview powered by FleetYards.net.
            </noscript>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Fleet() {
  const [ships, setShips] = useState([]);
  const [fleetStats, setFleetStats] = useState(null);
  const [topModels, setTopModels] = useState([]);
  const [manufacturerStats, setManufacturerStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [filters, setFilters] = useState({ search: "", classification: "", manufacturer: "", sort: "" });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [fleetchartOpen, setFleetchartOpen] = useState(false);
  const fleetchartTriggerRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    let mounted = true;

    fetchFleetStatsCached()
      .then((statsData) => {
        if (!mounted) return;
        setFleetStats(statsData?.stats || null);
        setTopModels(
          Array.isArray(statsData?.topModels)
            ? statsData.topModels
              .map((item) => ({
                slug: String(item?.slug || "").trim(),
                name:
                  (typeof item?.name === "string" && item.name.trim())
                  || formatModelSlugFallback(String(item?.slug || ""))
                  || String(item?.slug || "").trim(),
                count: Number(item?.count) || 0,
              }))
              .filter((item) => item.slug && item.count > 0)
            : []
        );
        setManufacturerStats(Array.isArray(statsData?.manufacturers) ? statsData.manufacturers : []);
      })
      .catch((err) => {
        console.error("Failed to fetch fleet aggregate stats", err);
        if (!mounted) return;
        setFleetStats(null);
        setTopModels([]);
        setManufacturerStats([]);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    loadFleetyardsScriptOnce().catch((error) => {
      console.error("Failed to preload Fleetyards embed script", error);
    });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("perPage", String(perPage));
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (filters.classification) params.set("classification", filters.classification);
    if (filters.manufacturer) params.set("manufacturer", filters.manufacturer);
    if (filters.sort) params.set("sort", filters.sort);
    setLoading(true);
    setLoadError("");
    const controller = new AbortController();
    fetch(`/api/fleet/grouped?${params.toString()}`, { signal: controller.signal })
      .then((res) => res.ok ? res.json() : Promise.reject(new Error(`Failed to fetch grouped fleet: HTTP ${res.status}`)))
      .then((data) => {
        setShips(Array.isArray(data?.rows) ? data.rows : []);
        setTotal(Number(data?.total || 0));
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        console.error("Failed to fetch fleet", err);
        setShips([]);
        setTotal(0);
        setLoadError("Failed to load fleet data.");
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [debouncedSearch, filters.classification, filters.manufacturer, filters.sort, page, perPage]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filters.classification, filters.manufacturer, filters.sort, perPage]);

  const classifications = useMemo(
    () =>
      Array.isArray(fleetStats?.classifications)
        ? fleetStats.classifications
          .map((item) => item?.label || item?.name)
          .filter(Boolean)
        : [],
    [fleetStats]
  );

  const manufacturers = useMemo(
    () =>
      Array.isArray(manufacturerStats)
        ? manufacturerStats
          .map((item) => item?.manufacturer)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b))
        : [],
    [manufacturerStats]
  );

  const sortedTopModels = useMemo(
    () => topModels.slice().sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)).slice(0, 10),
    [topModels]
  );

  const fleetchartShips = useMemo(
    () => Array.from(new Set(ships.map((ship) => buildFleetyardsShipSlug(ship)).filter(Boolean))),
    [ships]
  );

  const fleetId = useMemo(() => {
    const rawFleetId = String(fleetStats?.fleetSlug || "").trim();
    return rawFleetId || "chaotik";
  }, [fleetStats]);

  const totalPages = perPage === "all" ? 1 : Math.max(1, Math.ceil(total / perPage));

  return (
    <Layout>
      <button
        ref={fleetchartTriggerRef}
        type="button"
        onClick={() => setFleetchartOpen(true)}
        className="fixed right-4 top-20 sm:right-6 sm:top-24 z-40 rounded-xl border border-red-300/70 bg-red-500/80 backdrop-blur-sm px-4 py-2.5 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(248,113,113,0.35)] hover:bg-red-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-200/80"
      >
        View Fleetchart
      </button>

      <FleetchartModal
        open={fleetchartOpen}
        onClose={() => setFleetchartOpen(false)}
        triggerRef={fleetchartTriggerRef}
        fleetId={fleetId}
        ships={fleetchartShips}
      />

      <div className="mt-10 w-full flex justify-center">
        <SectionBox className="max-w-7xl">
          <PageTitle className="font-orbitron text-center mb-8">Fleet</PageTitle>
          <PageText>Browse the CHAOTIK fleet catalog synced from FleetYards.</PageText>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white/5 border border-white/10 rounded-lg p-5">
              <h3 className="font-orbitron text-lg mb-3">Class Breakdown</h3>
              <ul className="space-y-2 text-sm text-white/80">
                {(Array.isArray(fleetStats?.classifications) ? fleetStats.classifications : []).slice(0, 8).map((item) => (
                  <li key={item?.name || item?.label} className="flex justify-between gap-2">
                    <span>{item?.label || item?.name || "Class"}</span>
                    <span>{Number(item?.count || 0).toLocaleString()}</span>
                  </li>
                ))}
                {!Array.isArray(fleetStats?.classifications) || fleetStats.classifications.length === 0 ? (
                  <li className="text-white/50">No classification data.</li>
                ) : null}
              </ul>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-lg p-5">
              <h3 className="font-orbitron text-lg mb-3">Top Models</h3>
              <ul className="space-y-2 text-sm text-white/80">
                {sortedTopModels.map((item) => (
                  <li key={item.slug} className="flex justify-between gap-2">
                    <span className="truncate">{item.name}</span>
                    <span>{item.count.toLocaleString()}</span>
                  </li>
                ))}
                {sortedTopModels.length === 0 ? <li className="text-white/50">No model count data.</li> : null}
              </ul>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-lg p-5">
              <h3 className="font-orbitron text-lg mb-3">Manufacturers</h3>
              <ul className="space-y-2 text-sm text-white/80">
                {manufacturerStats.slice(0, 10).map((item) => (
                  <li key={item.manufacturer} className="flex justify-between gap-2">
                    <span className="truncate">{item.manufacturer}</span>
                    <span>{Number(item.count || 0).toLocaleString()}</span>
                  </li>
                ))}
                {manufacturerStats.length === 0 ? <li className="text-white/50">No manufacturer data.</li> : null}
              </ul>
            </div>
          </div>

          <div className="mt-6 grid gap-3 md:grid-cols-5">
            <input className="rounded-lg bg-white/5 border border-white/10 p-3" placeholder="Search ship models" value={filters.search} onChange={(e)=>setFilters((f)=>({...f,search:e.target.value}))} />
            <select className="rounded-lg bg-slate-900/80 text-white border border-white/10 p-3" value={filters.classification} onChange={(e)=>setFilters((f)=>({...f,classification:e.target.value}))}>
              <option value="" className="bg-slate-900 text-white">All classes</option>
              {classifications.map((c)=><option key={c} value={c} className="bg-slate-900 text-white">{c}</option>)}
            </select>
            <select className="rounded-lg bg-slate-900/80 text-white border border-white/10 p-3" value={filters.manufacturer} onChange={(e)=>setFilters((f)=>({...f,manufacturer:e.target.value}))}>
              <option value="" className="bg-slate-900 text-white">All manufacturers</option>
              {manufacturers.map((m)=><option key={m} value={m} className="bg-slate-900 text-white">{m}</option>)}
            </select>
            <select className="rounded-lg bg-slate-900/80 text-white border border-white/10 p-3" value={String(perPage)} onChange={(e)=>setPerPage(e.target.value === "all" ? "all" : (Number.parseInt(e.target.value, 10) || 25))}>
              {[25, 50, 100, 200].map((size)=><option key={size} value={size} className="bg-slate-900 text-white">{size} per page</option>)}
              <option value="all" className="bg-slate-900 text-white">All</option>
            </select>
            <select className="rounded-lg bg-slate-900/80 text-white border border-white/10 p-3" value={filters.sort} onChange={(e)=>setFilters((f)=>({...f,sort:e.target.value}))}>
              <option value="" className="bg-slate-900 text-white">Sort: Most owned</option>
              <option value="name" className="bg-slate-900 text-white">Sort: Ship name (A–Z)</option>
              <option value="manufacturer" className="bg-slate-900 text-white">Sort: Manufacturer (A–Z)</option>
              <option value="classification" className="bg-slate-900 text-white">Sort: Class type</option>
            </select>
          </div>
          {loading ? <p className="mt-6 text-white/70">Loading fleet manifest…</p> : null}
          {!loading && loadError ? <p className="mt-6 text-rose-300">{loadError}</p> : null}
          {!loading && !loadError && ships.length === 0 ? (
            <p className="mt-6 text-white/70">No grouped fleet ship types found yet. Run a fleet sync in the portal settings.</p>
          ) : null}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {ships.map((ship) => (
              <FleetCard key={ship.key} ship={ship} />
            ))}
          </div>
          {!loading && !loadError && total > 0 ? (
            <div className="mt-8 flex items-center justify-between gap-3">
              <p className="text-sm text-white/70">
                Showing page {page} of {totalPages} ({total.toLocaleString()} grouped models)
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}
          <FleetyardsAttribution className="mt-8" />
        </SectionBox>
      </div>
    </Layout>
  );
}
